import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Row, Col, Progress, Button, Card, Tag, Spin } from "antd";
import {
  PlayCircleOutlined,
  ClockCircleOutlined,
  ArrowRightOutlined,
  UserOutlined,
  RightOutlined,
  DownloadOutlined,
  ShareAltOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import journeyService from "../../../services/journey";
import { capitalizeFirstLetter, formatDuration } from "../../../utils/utils";
import { FiLock, FiPlay, FiClock, FiSend, FiCheckCircle } from "react-icons/fi";
import { CustomButton } from "../physician_page/styled";
import ViewVideoModal from "./modals/ViewVideoModal";
import { notify } from "../../../utils/helper";
import moment from "moment";
import { LuTrophy } from "react-icons/lu";
import { FaArrowLeftLong } from "react-icons/fa6";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { LuRefreshCw, LuPlay } from "react-icons/lu";
import PatientFeedbackModal from "./modals/PatientFeedbackModal";

const PublicPageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: var(--background-color, #ffffff);

  .journey-preview-row {
    @media (max-width: 768px) {
      padding: 12px 0 !important;
    }

    @media (max-width: 480px) {
      padding: 8px 0 !important;
      margin: 0 !important;
    }
  }

  .learning-path-title {
    @media (max-width: 768px) {
      font-size: 24px !important;
      margin-bottom: 14px !important;
    }

    @media (max-width: 480px) {
      font-size: 18px !important;
      margin-bottom: 12px !important;
      text-align: center !important;
    }
  }
`;

const PageWrapper = styled.div`
  padding: 32px 48px 64px;
  // background: #f8fafc;
  min-height: 100vh;
  text-align: center;

  @media (max-width: 768px) {
    padding: 20px 16px 32px;
  }

  @media (max-width: 480px) {
    padding: 20px 8px 20px;
  }

  .back-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    padding: 5px 10px;
    color: var(--primary-text-color);
    cursor: pointer;
    width: fit-content;
    border-radius: 6px;
    transition: all 0.3s;
    margin-bottom: 12px;
    position: absolute;
    top: 26px;

    @media (max-width: 768px) {
      top: 15px;
    }

    @media (max-width: 480px) {
      font-size: 11px;
      padding: 5px 10px;
      margin-bottom: 8px;
      top: 12px;
    }

    &:hover {
      background-color: var(--border-color);
    }
  }

  .welcome-badge-container {
    @media (max-width: 480px) {
      display: flex;
      justify-content: center;
      margin-bottom: 8px;
    }
  }

  .welcome-badge {
    @media (max-width: 480px) {
      font-size: 11px !important;
      padding: 5px 10px !important;
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
      line-height: 1.4;
    }
  }
`;

const HeaderTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: var(--primary-text-color);
  margin: 12px 0 5px;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 28px;
    margin: 8px 0 4px;
  }

  @media (max-width: 480px) {
    font-size: 22px;
    margin: 6px 0 4px;
    line-height: 1.3;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 16px;
  color: var(--secondary-text-color);
  margin: 0 0 16px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 480px) {
    font-size: 13px;
    margin: 0 0 14px;
    padding: 0 4px;
    line-height: 1.4;
  }
`;

const MessageBlock = styled.div`
  background-color: #eff6ff;
  color: #1e40af;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  text-align: left;
  border: 1px solid #dbeafe;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 480px) {
    font-size: 13px;
    padding: 10px 12px;
  }
`;

const InfoRow = styled.div`
  justify-content: center;
  display: flex;
  align-items: center;
  gap: 16px;
  color: var(--secondary-text-color);
  font-size: 14px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    gap: 8px;
    font-size: 11px;
    margin-bottom: 16px;
    padding: 0 4px;
  }
`;

const InfoItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const ProgressCard = styled(Card)`
  border: none;
  border-radius: 14px;
  background: linear-gradient(90deg, #4f46e5 0%, #8b5cf6 100%);
  color: white;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    margin-bottom: 20px;
    border-radius: 12px;
  }

  .ant-card-body {
    padding: 20px;

    @media (max-width: 768px) {
      padding: 18px;
    }

    @media (max-width: 480px) {
      padding: 14px;
    }
  }
  .ant-progress-bg {
    height: 12px !important;
    background-color: #8a3ef4 !important;

    @media (max-width: 480px) {
      height: 10px !important;
    }
  }

  .progress-header {
    @media (max-width: 768px) {
      gap: 16px;
    }

    @media (max-width: 480px) {
      flex-direction: row;
      gap: 10px;
      align-items: flex-start !important;
    }
  }

  .progress-percentage {
    @media (max-width: 480px) {
      align-self: flex-end;
    }
  }

  .progress-percent-text {
    @media (max-width: 480px) {
      font-size: 24px !important;
    }
  }

  .progress-complete-text {
    @media (max-width: 480px) {
      font-size: 12px !important;
    }
  }

  .next-step-container {
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 10px;
      align-items: flex-start !important;
    }

    @media (max-width: 480px) {
      margin-top: 10px;
      gap: 8px;
    }
  }

  .next-step-text {
    @media (max-width: 768px) {
      max-width: 100% !important;
    }

    @media (max-width: 480px) {
      font-size: 14px !important;
    }
  }

  .completion-message {
    @media (max-width: 480px) {
      font-size: 14px !important;
      flex-wrap: wrap;
      gap: 6px !important;
      margin-top: 10px !important;

      svg {
        width: 18px;
        height: 18px;
      }
    }
  }
`;

const ProgressLabel = styled.div`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 4px;
  color: white;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    margin-bottom: 3px;
  }
`;

const ProgressSub = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #dbeafe;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 15px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 6px;
  }
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 14px;
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const StepCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background-color: ${({ $isWatched, $isNext }) =>
    $isWatched
      ? "#f0fdf4" /* green */
      : $isNext
      ? "#eff6ff" /* blue */
      : "var(--)"} !important;
  margin-bottom: 0;

  @media (max-width: 480px) {
    border-radius: 10px;
  }

  .ant-card-body {
    padding: 20px 16px;

    @media (max-width: 768px) {
      padding: 18px 14px;
    }

    @media (max-width: 480px) {
      padding: 14px 10px;
    }
  }

  .custom-bg-blue {
    background-color: #2663eb !important;
  }
`;

const StepLayout = styled.div`
  display: flex;
  gap: 15px;
  align-items: start;

  @media (max-width: 768px) {
    gap: 12px;
  }

  @media (max-width: 480px) {
    gap: 10px;
    flex-wrap: nowrap;
  }
`;

const StepIndex = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 24px;
  background: ${({ $isWatched, $isNext }) =>
    $isWatched ? "#22c55e" : $isNext ? "#2663EB" : "var(--border-color)"};
  color: ${({ $isWatched, $isNext }) =>
    $isNext ? "#fff" : "var(--primary-text-color)"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;

  .check-icon {
    font-size: 26px;
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    border-radius: 22px;
    font-size: 15px;
  }

  @media (max-width: 480px) {
    width: 27px;
    height: 27px;
    border-radius: 18px;
    font-size: 13px;
    position: absolute;
    top: -1px;
    left: -1px;

    .check-icon {
      font-size: 16px;
    }
  }
`;

const VideoThumb = styled.div`
  width: 100%;
  max-width: 110px;
  height: 72px;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-image: ${({ thumbnail }) =>
  thumbnail ? `url(${thumbnail})` : `url('/no-video.png')`};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  font-size: 22px;
  flex-shrink: 0;

  .play-icon {
    color: white;
    font-size: 25px;
  }

  @media (max-width: 768px) {
    max-width: 95px;
    height: 64px;
    font-size: 20px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    height: 160px;
    font-size: 16px;
    border-radius: 6px;
  }
`;

const StepContent = styled.div`
  display: grid;
  flex-direction: column;
  text-align: left;
  gap: 6px;
  align-items: start;
  flex: 1;
  min-width: 0;

  @media (max-width: 480px) {
    width: 100%;
  }

  .step-title-row {
    @media (max-width: 768px) {
      flex-wrap: wrap;
    }

    @media (max-width: 480px) {
      // flex-direction: column;
      align-items: flex-start !important;
      gap: 6px;
    }
  }

  .step-title-text {
    @media (max-width: 768px) {
      max-width: 100% !important;
    }

    @media (max-width: 480px) {
      max-width: 100% !important;
      font-size: 13px !important;
    }
  }

  .next-step-tag {
    @media (max-width: 480px) {
      font-size: 10px !important;
      padding: 0 8px !important;
      line-height: 1.4;
    }
  }

  .step-clock-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    color: #4d5052;
    @media (max-width: 480px) {
      display: flex;
      font-size: 12px;
    }
  }

  .completed-status {
    @media (max-width: 480px) {
      font-size: 12px !important;
      flex-wrap: wrap;

      svg {
        width: 14px;
        height: 14px;
      }
    }
  }

  .step-actions {
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 8px;
      align-items: stretch !important;
    }

    @media (max-width: 480px) {
      gap: 8px;
    }
  }

  .watch-again-btn,
  .watch-video-btn,
  .send-video-btn {
    @media (max-width: 768px) {
      width: 100%;
      justify-content: center;
      padding: 8px 12px !important;
      font-size: 13px !important;
    }

    @media (max-width: 480px) {
      padding: 7px 10px !important;
      font-size: 12px !important;
      min-height: 36px;
    }
  }

  .send-btn {
    display: flex;
    align-items: center;
    overflow: hidden;
    transition: all 0.2s;
  }
  .send-btn span {
    display: block;
    transition: all 0.3s ease-in-out;
  }
  .send-btn .send-icon {
    display: block;
    transform-origin: center center;
    transition: transform 0.3s ease-in-out;
  }

  .send-btn:hover .icon-wrapper {
    animation: fly-1 0.6s ease-in-out infinite alternate;
  }
  .send-btn:hover .send-icon {
    transform: translateX(2.2em) rotate(45deg) scale(1.1);
  }
  .send-btn:hover span {
    transform: translateX(6em);
  }

  @keyframes fly-1 {
    from {
      transform: translateY(0.1em);
    }

    to {
      transform: translateY(-0.1em);
    }
  }

  .resend-btn {
    display: flex;
    align-items: center;
    overflow: hidden;
    transition: all 0.2s;
    background-color: rgba(24, 144, 255, 0.1) !important;
    color: #1890ff !important;
    border: 1px solid #1890ff !important;

    .send-icon {
      display: block;
      transform-origin: center center;
      transition: transform 0.4s ease;
    }

    &:hover .send-icon {
      animation: spin 0.9s linear infinite;
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const StepTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--primary-text-color);
  display: flex !important;
  align-items: center !important;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    gap: 4px;
    width: 100%;
  }
`;

const StepDesc = styled.div`
  font-size: 14px;
  color: var(--secondary-text-color);
  margin-bottom: 16px;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 14px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    margin-bottom: 10px;
    line-height: 1.4;
  }
`;

const PrimaryButton = styled.div`
  background: white;
  border: none;
  color: #2563eb;
  font-size: 14px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 6px 12px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 6px 10px;
    width: 100%;
    justify-content: center;
  }

  &:hover {
    background: #eff6ff;
  }
`;

const CompletionCard = styled.div`
  background: #22C55E ;
  background: linear-gradient(90deg,rgba(34, 197, 94, 1) 60%, rgba(5, 150, 105, 1) 90%);
  color: #fff;
  border-radius: 8px;
  padding: 32px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 24px;
  }

  @media (max-width: 480px) {
    padding: 20px 16px;
  }

  h2 {
    font-size: 24px;
    margin-bottom 8px;
    color: #fff;

    @media (max-width: 480px) {
      font-size: 20px;
      margin-bottom: 6px;
    }
  }
  p {
    color: #dcfce7;
    margin-bottom: 24px;
    font-size: 16px;

    @media (max-width: 480px) {
      font-size: 14px;
      margin-bottom: 16px;
    }
  }

  .btn-box {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    flex-wrap: wrap;

    @media (max-width: 480px) {
      gap: 12px;
      flex-direction: column;
      width: 100%;
    }

    .btn-first {
      background-color: #fff;
      color: #16a34a;
      font-size: 14px;
      font-weight: 500;
      padding: 8px 16px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      line-height: 20px;

      @media (max-width: 480px) {
        width: 100%;
        justify-content: center;
        padding: 10px 16px;
      }

      &:hover{
        background-color: #f0fdf4;
      }
    }
    .btn-second {
      background-color: transparent;
      color: #fff;
      border: 1px solid #fff;
      font-size: 14px;
      font-weight: 500;
      padding: 8px 16px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      line-height: 20px;

      @media (max-width: 480px) {
        width: 100%;
        justify-content: center;
        padding: 10px 16px;
      }

      &:hover{
        background-color: #fff;
        color: #16a34a;
      }
    }
  }
`;

const CardContent = styled.div`
  display: flex;
  align-items: start;
  justify-content: center;
  width: 100%;
  gap: 16px;

  @media (max-width: 480px) {
    gap: 12px;
    flex-direction: column;
  }
`;

const JourneyPreviewPage = () => {
  const { id } = useParams();
  const [journey, setJourney] = useState(null);
  const [journeyDataFromAssignment, setJourneyDataFromAssignment] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStep, setSelectedStep] = useState([]);
  const [showVideo, setShowVideo] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [isFeedbackModal, setIsFeedbackModal] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const assignmentId = searchParams.get("id");
  const isAssignmentRoute = pathname.match(/^\/journey\/([^/]+)/);

  // Use assignmentId from query params, or from route if it's /journey/:id
  const assignmentIdToUse = assignmentId || (isAssignmentRoute ? id : null);
  const videoId = searchParams.get("videoId");

  // Extract assignmentId from the URL query parameters and call the API accordingly
  const getJourneyByAssignmentId = async (assignmentIdToUse) => {
    try {
      const res = await journeyService.getJourneyByAssignmentId(
        assignmentIdToUse
      );

      if (res?.data) {
        setJourneyDataFromAssignment(true);
        setJourney(res?.data?.assignment);
      }
    } catch (error) {
      console.error("Error fetching journey by assignment ID:", error);
      // keep as null
    }
  };

  const fetchAssignment = async () => {
    try {
      setLoading(true);
      const res = await journeyService.getAssignment(id);
      if (res?.data) {
        setJourney(res?.data?.assignment);
      }
    } catch (error) {
      console.error("Error fetching journey:", error);
      // keep as null
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;

    // If it's the /journey/:id route or has assignmentId query param, use assignment API
    const isAssignmentRoute = pathname.match(/^\/journey\/([^/]+)/);
    if (isAssignmentRoute || assignmentId) {
      getJourneyByAssignmentId(assignmentIdToUse);
    } else {
      // Otherwise, use regular journey API
      fetchAssignment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pathname, assignmentIdToUse]);

  const handleClickVideo = async (step) => {
    if (!journeyDataFromAssignment) return;
    const journeyId = journey?.id || journey?._id;

    const payload = {
      videoId: step?.video?.id || step?.video?._id,
    };
    try {
      await journeyService.clickVideo(journeyId, payload);
    } catch (error) {
      notify("error", error.message);
    }
  };

  const playVideo = (step) => {
    if(journeyDataFromAssignment) {
      handleClickVideo(step);
    }
    setSelectedStep(step);
    setShowVideo(true);
  };

  const handleSendVideo = async (journeyData, video) => {
    if (!journeyData) return;

    const patientId = journeyData?.patientId?.id;
    try {
      const payload = {
        journeyId: journeyData?.journey?.id,
        patientIds: [patientId],
        scheduleDate: moment().toISOString(),
        sendVideo: video ? video?.id : "full",
      };
      setSendingId(video?.id);
      await journeyService.sendJourney(payload);
      notify("success", "Video send successfully");
      fetchAssignment();
      setSendingId(null);
    } catch (error) {
      setSendingId(null);
    }
  };

  const handleFeedbackModalOpen = () => {
    if (!journeyDataFromAssignment) return;
    setTimeout(() => {
      setIsFeedbackModal(true);
    }, 500);
  };

  const journeySteps = journey;
  const steps = journey?.steps || [];
  const completed = journey?.watchedCount;
  const totalSteps = steps.length || 0;
  const progress =
    totalSteps > 0 ? Math.round((completed / totalSteps) * 100) : 0;

  const getNextStep = steps.findIndex((step) => {
    const videoStatus = journeySteps?.steps?.find(
      (s) => (s.video?.id || s.video?._id) === (step?.video?.id || step?.video?._id)
    );
    return videoStatus?.status !== "watched";
  });

  // Check if this is a public route (assignment route)
  const isPublicRoute = pathname.match(/^\/journey\/([^/]+)/);
  const type = searchParams.get("type");
  const stepVideoId = searchParams.get("videoId");

  const sendSingleVideo = type === "video" && !!stepVideoId;
  const sendAllvideo = type === "journey" && !stepVideoId;

  const singleVideo = sendSingleVideo 
  ? steps?.find(step => (step.video?.id || step.video?._id) === stepVideoId)
  : null;

  const pageContent = (
    <>
      {showVideo && (
        <ViewVideoModal
          isOpen={showVideo}
          setOpen={setShowVideo}
          videoData={selectedStep}
          journeyData={journey}
          fetchAssignment={journeyDataFromAssignment ? 
            () => getJourneyByAssignmentId(assignmentIdToUse) : fetchAssignment}
          onSuccess={() => handleFeedbackModalOpen()}
          isPublicRoute={isPublicRoute}
        />
      )}

      {isFeedbackModal && (
        <PatientFeedbackModal 
          isOpen={isFeedbackModal}
          setIsOpen={setIsFeedbackModal}
          videoData={selectedStep}
          journeyData={journey}
        />
      )}
      <Row
        className="journey-preview-row"
        style={{
          // backgroundColor: "#f8fafc",
          padding: "20px 0",
          width: "100%",
          borderRadius: 10,
        }}
      >
        <Col
          xs={{ span: 24, offset: 0 }}
          sm={{ span: 24, offset: 0 }}
          md={{ span: 20, offset: 2 }}
          lg={{ span: 16, offset: 4 }}
        >
          <PageWrapper>
            {!journeyDataFromAssignment && (
              <div className="back-btn" onClick={() => window.history.back()}>
                <FaArrowLeftLong />
                Go back
              </div>
            )}
            <div className="welcome-badge-container">
              <span
                className="welcome-badge"
                style={{
                  fontSize: 14,
                  backgroundColor: "var(--border-color)",
                  border: "1px solid var(--border-cololr)",
                  color: "var(--primary-text-color)",
                  padding: "8px 16px",
                  borderRadius: 22,
                }}
              >
                <UserOutlined style={{ marginRight: "5px" }} />
                Welcome back,&nbsp;
                {journey?.patientId ? journey.patientId?.name : "User"}
              </span>
            </div>

            <HeaderTitle>
              {journey?.journey?.name || "Journey Title"}
            </HeaderTitle>
            <HeaderSubtitle>
              {capitalizeFirstLetter(
                journey?.journey?.description ||
                  "Comprehensive plan description goes here."
              )}
            </HeaderSubtitle>

            <InfoRow>
              <InfoItem>
                <span role="img" aria-label="doctor">
                  👤
                </span>
                {`Dr.${" "}${journey?.journey?.coordinatorId?.name || journey?.journey?.physicianId?.name}` ||
                  "Dr. Michael Johnson"}
              </InfoItem>
              <InfoItem>
                <ClockCircleOutlined />
                {journey?.durationText || "Ongoing"}
              </InfoItem>
              <InfoItem>
                <span role="img" aria-label="specialty">
                  🩺
                </span>
                {journey?.specialty ||
                  journey?.steps?.[0]?.video?.specialty ||
                  "Endocrinology"}
              </InfoItem>
            </InfoRow>

            {journey?.message && (
              <MessageBlock>
                <InfoCircleOutlined
                  style={{ fontSize: "18px", marginTop: "2px" }}
                />
                <div className="d-flex align-start flex-column">
                  <span style={{ fontWeight: 600, marginBottom: "2px" }}>
                    {`Message from ${journey?.journey?.coordinatorId?.name || journey?.journey?.physicianId?.name}`}
                  </span>
                  <span style={{ lineHeight: "1.4" }}>{journey.message}</span>
                </div>
              </MessageBlock>
            )}

            {(!journeyDataFromAssignment ||
              (journeyDataFromAssignment &&
                !sendSingleVideo &&
                sendAllvideo)) && (
              <ProgressCard>
                <div className="d-flex justify-between progress-header">
                  <div>
                    <ProgressLabel>Your Progress</ProgressLabel>
                    <ProgressSub>
                      {completed} of {totalSteps} steps completed
                    </ProgressSub>
                  </div>
                  <div className="d-flex flex-column progress-percentage">
                    <span
                      className="font-bold font-30 progress-percent-text"
                      style={{ color: "#fff" }}
                    >
                      {progress} %
                    </span>
                    <span
                      className="progress-complete-text"
                      style={{ color: "#DBEAFE" }}
                    >
                      Complete
                    </span>
                  </div>
                </div>
                <Progress
                  percent={progress}
                  showInfo={false}
                  strokeColor="#c4b5fd"
                  trailColor="rgba(255,255,255,0.35)"
                />
                {journeySteps?.isCompleted ? (
                  <div
                    className="d-flex font-16 font-medium completion-message"
                    style={{
                      justifyContent: "start",
                      marginTop: "12px",
                      gap: "8px",
                      color: "#fef08a",
                    }}
                  >
                    <LuTrophy />
                    <span className="font-medium">
                      Journey Completed! Congratulations! 🎉
                    </span>
                  </div>
                ) : (
                  <div
                    className="next-step-container"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 12,
                    }}
                  >
                    <div
                      className=" d-flex font-16 next-step-text"
                      style={{
                        gap: "8px",
                        color: "#DBEAFE",
                        justifyContent: "flex-start",
                        maxWidth: "300px",
                      }}
                    >
                      <RightOutlined style={{ fontSize: "12px" }} />
                      Next:{" "}
                      <span className="ellipsis">
                        {steps[completed]?.video?.title ||
                          "Understanding Diabetes"}
                      </span>
                    </div>
                    <PrimaryButton onClick={() => playVideo(steps[completed])}>
                      Continue Learning
                      {/* <ArrowRightOutlined /> */}
                    </PrimaryButton>
                  </div>
                )}
                {/* <div
              style={{
                marginTop: 12,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <PrimaryButton>
                Continue Learning <ArrowRightOutlined />
              </PrimaryButton>
            </div> */}
              </ProgressCard>
            )}

            <h3
              className="learning-path-title"
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: "var(--primary-text-color)",
                marginBottom: 16,
                textAlign: "left",
              }}
            >
              Your Learning Path
            </h3>

            <StepsList>
              {steps.length === 0 && (
                <StepCard>
                  <div>No steps available yet.</div>
                </StepCard>
              )}
              {(singleVideo ? [singleVideo] : steps)?.map((step, index) => {
                const isNext = index === getNextStep;
                const videoStatus = journeySteps?.steps?.find(
                  (s) =>
                    (s.video?._id || s.video?.id) ===
                    (step?.video?._id || step?.video?.id)
                );
                const isWatched = videoStatus?.status === "watched";
                const isSending = sendingId === step?.video?.id;
                const isAlreadySend = videoStatus?.sendAt;

                return (
                  <StepCard
                    $isNext={isNext}
                    $isWatched={isWatched}
                    key={step.id || step._id || index}
                    style={{
                      borderColor: isWatched
                        ? "#bbf7d0"
                        : isNext
                        ? "#bfdbfe"
                        : "",
                      boxShadow: isNext ? "0 0 0 2px #bfdbfe" : "",
                    }}
                  >
                    <StepLayout>
                      <StepIndex $isNext={isNext} $isWatched={isWatched}>
                        {isWatched ? (
                          <FiCheckCircle className="check-icon" color="#fff" />
                        ) : (
                          `${index + 1}`
                        )}
                      </StepIndex>
                      <CardContent>
                      <VideoThumb
                        key={index}
                        thumbnail={step?.video?.thumbnail}
                        onClick={() => playVideo(step)}
                      >
                        <LuPlay className="play-icon" />
                      </VideoThumb>
                      <StepContent>
                        <div className="d-flex justify-between w-100 step-title-row">
                          <StepTitle>
                            <span
                              style={{
                                color: isWatched || isNext ? "#020817" : "",
                                maxWidth: "250px",
                              }}
                              className="ellipsis step-title-text"
                            >
                              {capitalizeFirstLetter(
                                step.video?.title || `Step ${index + 1}`
                              )}
                            </span>
                            {isNext && (
                              <Tag
                                color="#2663EB"
                                style={{ borderRadius: 30, padding: "0 10px" }}
                                className="next-step-tag"
                              >
                                Next Step
                              </Tag>
                            )}
                          </StepTitle>
                          <div className="step-clock-icon">
                            {formatDuration(step?.video?.duration)}
                            <FiClock />
                          </div>
                        </div>
                        <StepDesc
                          style={{ maxWidth: "80%" }}
                          className="ellipsis step-description"
                        >
                          {capitalizeFirstLetter(
                            step.video?.description ||
                              "Description for this step will appear here."
                          )}
                        </StepDesc>

                        {isWatched && (
                          <div
                            className="d-flex completed-status"
                            style={{
                              fontSize: "14px",
                              color: "#16a34a",
                              gap: 5,
                              justifyContent: "flex-start"
                            }}
                          >
                            <FiCheckCircle size={17} />
                            <span>
                              Completed on{" "}
                              {moment(videoStatus.watchedAt).format(
                                "DD/MM/YYYY"
                              )}
                            </span>
                          </div>
                        )}

                        <div
                          className="d-flex justify-between w-100 step-actions"
                          style={{ marginTop: 8 }}
                        >
                          {/* <Button
                              icon={<PlayCircleOutlined />}
                              type="primary"
                              style={{
                                background: "#4f46e5",
                                borderColor: "#4f46e5",
                                color: "white",
                              }}
                            >
                            {index === completed ? "Start Learning" : "Watch Video"}
                          </Button> */}
                          {isWatched ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => playVideo(step)}
                              className="watch-again-btn"
                              style={{
                                padding: "5px 12px",
                                fontSize: "14px",
                                height: "auto",
                                fontWeight: 500,
                                border: "1px solid #bbf7d0",
                                color: "#16a34a",
                              }}
                            >
                              <FiPlay />
                              Watch Again
                            </Button>
                          ) : (
                            <CustomButton
                              onClick={() => playVideo(step)}
                              size="sm"
                              className={`watch-video-btn ${
                                isNext ? "custom-bg-blue" : ""
                              }`}
                              style={{
                                padding: "5px 12px",
                                fontSize: "14px",
                                height: "auto",
                                fontWeight: 500,
                              }}
                            >
                              <FiPlay />
                              {isNext ? "Start Learning" : "Watch Video"}
                            </CustomButton>
                          )}
                          {/* // : (
                          //   <Button variant="outline" size="sm" disabled>
                          //     <FiLock />
                          //     Locked
                          //   </Button>
                          // )} */}
                          {!journeyDataFromAssignment && !isWatched && (
                            <CustomButton
                              onClick={() =>
                                handleSendVideo(journey, step.video)
                              }
                              size="sm"
                              className={`send-video-btn ${
                                isAlreadySend
                                  ? "resend-btn"
                                  : !isSending && "send-btn"
                              }`}
                              style={{
                                padding: "5px 12px",
                                fontSize: "14px",
                                height: "auto",
                                fontWeight: 500,
                                borderRadius: isSending ? "20px" : "",
                              }}
                              disabled={isSending}
                            >
                              {isSending ? (
                                <Spin
                                  style={{
                                    color: isAlreadySend ? "#1890ff" : "#fff",
                                  }}
                                  indicator={<LoadingOutlined spin />}
                                />
                              ) : (
                                <div className="d-flex">
                                  <div className="icon-wrapper">
                                    {isAlreadySend ? (
                                      <LuRefreshCw className="send-icon" />
                                    ) : (
                                      <FiSend className="send-icon" />
                                    )}
                                  </div>
                                  <span>
                                    {isAlreadySend ? "Resend Now" : "Send Now"}
                                  </span>
                                </div>
                              )}
                            </CustomButton>
                          )}
                        </div>
                      </StepContent>
                      </CardContent>
                    </StepLayout>
                  </StepCard>
                );
              })}
            </StepsList>

            {/* Send All Video Button */}
            {journey?.journey?.type === "clinical" &&
              !journeyDataFromAssignment && !journeySteps?.isCompleted && (
                <Row
                  className="d-flex mt-16"
                  style={{ justifyContent: "center" }}
                >
                  <CustomButton
                    onClick={() => handleSendVideo(journey)}
                    size="sm"
                    style={{
                      padding: "9px 25px",
                      fontSize: "17px",
                      height: "auto",
                      fontWeight: 500,
                      borderRadius: "30px",
                    }}
                  >
                    <FiSend />
                    Send All Now
                  </CustomButton>
                </Row>
              )}
            {/* Journey Completion Card */}
            {journeySteps?.isCompleted && (
              <CompletionCard className="mt-16 bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0">
                <LuTrophy
                  style={{
                    fontSize: "60px",
                    color: "#fde047",
                    marginBottom: "12px",
                  }}
                />
                <h2 className="font-bold">Congratulations! 🎉</h2>
                <p className="text-green-100 mb-16">
                  Thanks for completing your journey to a healthier life!
                </p>
                {/* <div className="btn-box">
                  <div variant="secondary" className="btn-first">
                    <DownloadOutlined className="font-18" />
                    Download Certificate
                  </div>
                  <div
                    variant="outline"
                    className="d-flex btn-second font-14 font-semibold"
                  >
                    <ShareAltOutlined className="font-18" />
                    Share Achievement
                  </div>
                </div> */}
              </CompletionCard>
            )}
          </PageWrapper>
        </Col>
      </Row>
    </>
  );

  // If it's a public route, wrap it in PublicPageWrapper (no navbar)
  // Otherwise, return as is (will be wrapped by Main component with navbar)
  if (isPublicRoute) {
    return <PublicPageWrapper>{pageContent}</PublicPageWrapper>;
  }

  return pageContent;
};

export default JourneyPreviewPage;
